import { Card, Col, DatePicker, Empty, Row, Statistic, Table, Typography, theme as antdTheme } from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsApi, type AgingRow } from '@/api/reports';
import { PageHeader } from '@/shared/components/page-header';
import { MaskedAmount, RevealGroup, RevealToggle } from '@/shared/components/masked-amount';
import { useSchoolTheme } from '@/themes/use-theme';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function ReceivablesAgingPage() {
  const { token } = antdTheme.useToken();
  const { chartColors } = useSchoolTheme();
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());
  const asOfStr = asOf.format('YYYY-MM-DD');

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'receivables-aging', asOfStr],
    queryFn: () => reportsApi.receivablesAging({ asOf: asOfStr }),
  });

  const summaryData = data
    ? [
        { bucket: 'Current',  amount: Number(data.current) },
        { bucket: '0-30 d',   amount: Number(data.days0to30) },
        { bucket: '31-60 d',  amount: Number(data.days31to60) },
        { bucket: '61-90 d',  amount: Number(data.days61to90) },
        { bucket: '>90 d',    amount: Number(data.over90) },
      ]
    : [];

  const columns: TableProps<AgingRow>['columns'] = [
    {
      title: 'Class · Section',
      render: (_, r) => (
        <Typography.Text strong>
          {r.className} · {r.sectionName}
        </Typography.Text>
      ),
      width: 200,
    },
    { title: 'Defaulters', dataIndex: 'studentCount', width: 110, align: 'right' as const },
    { title: 'Current',  dataIndex: 'current',    width: 130, align: 'right' as const, render: (v: string) => <MaskedAmount value={`₨ ${PKR(v)}`} /> },
    { title: '0-30 d',   dataIndex: 'days0to30',  width: 130, align: 'right' as const, render: (v: string) => <MaskedAmount value={`₨ ${PKR(v)}`} /> },
    { title: '31-60 d',  dataIndex: 'days31to60', width: 130, align: 'right' as const, render: (v: string) =>
        <span style={{ color: Number(v) > 0 ? token.colorWarning : undefined }}><MaskedAmount value={`₨ ${PKR(v)}`} /></span> },
    { title: '61-90 d',  dataIndex: 'days61to90', width: 130, align: 'right' as const, render: (v: string) =>
        <span style={{ color: Number(v) > 0 ? token.colorWarning : undefined }}><MaskedAmount value={`₨ ${PKR(v)}`} /></span> },
    { title: '>90 d',    dataIndex: 'over90',     width: 130, align: 'right' as const, render: (v: string) =>
        <span style={{ color: Number(v) > 0 ? token.colorError : undefined, fontWeight: Number(v) > 0 ? 600 : undefined }}><MaskedAmount value={`₨ ${PKR(v)}`} /></span> },
    {
      title: 'Total',
      dataIndex: 'total',
      width: 140,
      align: 'right' as const,
      render: (v: string) => <Typography.Text strong><MaskedAmount value={`₨ ${PKR(v)}`} /></Typography.Text>,
    },
  ];

  return (
    <RevealGroup>
      <div style={{ display: 'grid', gap: 18 }}>
        <PageHeader
          eyebrow="Reports / Finance"
          title="Receivables aging"
          description="Open fee invoices bucketed by how overdue they are. Useful for the Accountant's morning collection list."
        />

        <Card>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={6}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>As of</Typography.Text>
              <DatePicker
                style={{ width: '100%' }}
                value={asOf}
                onChange={(v) => v && setAsOf(v)}
                allowClear={false}
                format="D MMM YYYY"
              />
            </Col>
            <Col xs={24} md={18} style={{ textAlign: 'right' }}>
              <RevealToggle />
            </Col>
          </Row>
        </Card>

        {data && (
          <Card>
            <Row gutter={[16, 12]}>
              <Col xs={12} md={4}>
                <Statistic
                  title="Current"
                  value={`₨ ${PKR(data.current)}`}
                />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="0-30 days" value={`₨ ${PKR(data.days0to30)}`} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="31-60 days" value={`₨ ${PKR(data.days31to60)}`} styles={{ content: { color: token.colorWarning } }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="61-90 days" value={`₨ ${PKR(data.days61to90)}`} styles={{ content: { color: token.colorWarning } }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title=">90 days" value={`₨ ${PKR(data.over90)}`} styles={{ content: { color: token.colorError } }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="Grand total" value={`₨ ${PKR(data.grandTotal)}`} styles={{ content: { fontWeight: 700 } }} />
              </Col>
            </Row>
          </Card>
        )}

        {data && Number(data.grandTotal) > 0 && (
          <Card title={<Typography.Text strong>Bucket distribution</Typography.Text>}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={summaryData}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                <XAxis dataKey="bucket" stroke={token.colorTextSecondary} />
                <YAxis stroke={token.colorTextSecondary} tickFormatter={(v) => PKR(v as number)} />
                <Tooltip
                  formatter={(v: number) => `₨ ${PKR(v)}`}
                  contentStyle={{
                    background: token.colorBgElevated,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                />
                <Bar dataKey="amount" fill={chartColors[0]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        <Card title={<Typography.Text strong>Per class·section</Typography.Text>}>
          {data && data.rows.length === 0 ? (
            <Empty description="No outstanding receivables 🎉" />
          ) : (
            <Table<AgingRow>
              rowKey="classSectionId"
              dataSource={data?.rows ?? []}
              loading={isFetching}
              columns={columns}
              pagination={false}
              size="small"
              scroll={{ x: 1100 }}
            />
          )}
        </Card>
      </div>
    </RevealGroup>
  );
}
